import * as React from 'react'
import { PageHeader, Well } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { observer } from 'mobx-react'
import { Loading3QuartersOutlined, DollarOutlined } from '@ant-design/icons'
import { Dropdown, Button, Table, DatePicker, Menu } from 'antd'
import './style/batch-billing.scss'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { ColumnProps } from 'antd/lib/table'
import titleCase from 'title-case'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { isDef } from '@pushly/aqe/lib/utils/is-def'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import clone from 'clone-deep'
import { AccountChargeForm } from '@halo/components/account-charge-form/account-charge-form'
import moment, { Moment } from 'moment-timezone'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { PropsWithChildren } from 'react'

class BillingTableDataDto extends AccountDto {
    public static fromAccountDto(fields: Partial<BillingTableDataDto>): BillingTableDataDto {
        const dto = new BillingTableDataDto()
        Object.assign(dto, fields, {
            key: fields.id,
        })
        return dto
    }

    public readonly key: number

    public loadingDependecies: boolean = true
    public lastBillingRecord: any
    public lastMonthsBillableSubscriberCount: any
}

class CompoundTableWrapper extends React.PureComponent<PropsWithChildren<any>> {
    public render() {
        return (
            <div className={classnames('compound-table')}>
                <div className={classnames('compound-table-wrapper')}>{this.props.children}</div>
            </div>
        )
    }
}

interface IHeader {
    left?: React.ReactNode
    right?: React.ReactNode
}

class Header extends React.PureComponent<IHeader> {
    public render() {
        return (
            <div className={classnames('compound-table-header')}>
                <div className={classnames('compound-table-header-wrapper')}>
                    {isDef(this.props.left) && (
                        <div className={classnames('compound-table-header-left')}>{this.props.left}</div>
                    )}
                    {isDef(this.props.right) && (
                        <div className={classnames('compound-table-header-right')}>{this.props.right}</div>
                    )}
                </div>
            </div>
        )
    }
}

interface IColumnProps extends ColumnProps<BillingTableDataDto> {
    dataKey?: string
}

interface IAccounts {}

interface IAccountsState {
    loading: boolean
    accountsLoaded: boolean
    allAccounts: any[]
    visibleAccounts: any[]
    accountsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string

    unchargedSince: string
    activeAccount?: AccountDto
}

@observer
export class BatchBilling extends React.Component<IAccounts, IAccountsState> {
    protected appService: AppService
    protected accountService: AccountService

    public constructor(props: any) {
        super(props)

        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)

        this.state = {
            page: 1,
            pageSize: 15,
            loading: true,
            accountsLoaded: false,
            allAccounts: [],
            visibleAccounts: [],
            accountsMeta: {},
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
            unchargedSince: this.lastMonthValue.format('YYYY-MM'),
        }
    }

    public componentDidMount(): void {
        this.ensureAccountsForList()
    }

    public async ensureAccountsForList() {
        await this.fetchAccounts()
    }

    public render() {
        let { sortedInfo } = this.state
        sortedInfo = sortedInfo || {}

        const buildColumn = (config: IColumnProps): React.ReactNode => {
            const title = (config.title as string) || titleCase(config.dataKey || (config.key as string))
            if (config.sorter) {
                config.sortOrder = sortedInfo.columnKey === (config.dataKey || config.key) && sortedInfo.order
            }

            return (
                <Table.Column
                    {...config}
                    title={title}
                    dataIndex={config.dataKey}
                    key={config.dataKey || config.key}
                    className={config.className || config.dataKey || (config.key as string)}
                />
            )
        }

        return (
            <div className={classnames('page', 'batch-billing')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader title="Batch Billing" onTitleSet={noop} />

                    <Well className="type-2" title="Billable Accounts" hideHeader={true} hideFooter={true}>
                        <CompoundTableWrapper>
                            <Header
                                right={
                                    <>
                                        <span>Show uncharged accounts for: </span>
                                        <DatePicker.MonthPicker
                                            size="small"
                                            placeholder=""
                                            format="MMMM, YYYY"
                                            defaultValue={this.lastMonthValue}
                                            disabledDate={(current: Moment) => {
                                                return current.startOf('month') > this.lastMonthValue
                                            }}
                                            onChange={this.updateSelectedPeriod}
                                        />
                                    </>
                                }
                            />
                            <Table
                                size="small"
                                bordered={false}
                                dataSource={this.state.visibleAccounts}
                                onChange={this.handleChange}
                                pagination={false}
                                loading={
                                    this.isLoading && {
                                        indicator: <Loading3QuartersOutlined spin={true} />,
                                    }
                                }
                                locale={{
                                    emptyText: <AntdTableEmptyPlaceholder text="No Accounts" icon="dollar" />,
                                }}
                            >
                                {buildColumn({
                                    className: 'account',
                                    dataKey: 'display_name',
                                    title: 'Organization',
                                    render: this.renderDisplay,
                                })}
                                {buildColumn({
                                    key: 'last_billed_at',
                                    title: 'Last Billed',
                                    render: this.renderLastBilledAt,
                                })}
                                {buildColumn({
                                    key: 'actions',
                                    title: '',
                                    render: (notification) => this.renderRowActions(notification),
                                    align: 'center',
                                })}
                            </Table>
                        </CompoundTableWrapper>
                    </Well>

                    <AccountChargeForm
                        key={`charge-form-act${this.state.activeAccount ? this.state.activeAccount.id : '0'}-${
                            this.state.unchargedSince || '0'
                        }`}
                        visible={!!this.state.activeAccount}
                        account={this.state.activeAccount as any}
                        onClose={() => this.setState({ activeAccount: undefined })}
                        onSuccess={noop}
                        defaultBillingPeriod={this.state.unchargedSince}
                    />
                </div>
            </div>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading && !this.state.accountsLoaded
    }

    protected get lastMonthValue(): Moment {
        return moment().subtract(1, 'month').startOf('month')
    }

    protected updateSelectedPeriod = async (billablePeriod: Moment | undefined): Promise<void> => {
        if (!!billablePeriod) {
            await this.setState({ unchargedSince: billablePeriod.startOf('month').format('YYYY-MM') })
        } else {
            await this.setState({ unchargedSince: undefined })
        }

        return this.fetchAccounts()
    }

    protected handleDateSort = (a: BillingTableDataDto, b: BillingTableDataDto): number => {
        const aCreated = moment(a.created_at)
        const bCreated = moment(b.created_at)

        return aCreated > bCreated ? 1 : aCreated < bCreated ? -1 : 0
    }

    protected renderDisplay = (text: string, account: BillingTableDataDto): React.ReactNode => {
        const id = account.id
        const name = account.name

        return <TableRowEntityDisplay title={name} />
    }

    protected renderLastBilledAt = (text: string, account: BillingTableDataDto): React.ReactNode => {
        if (account.loadingDependecies) {
            return <Loading3QuartersOutlined spin={true} />
        }

        if (!account.lastBillingRecord) {
            return '--'
        }

        let receiptUrl: string
        if (account.lastBillingRecord.stripe_charge_receipt_url) {
            receiptUrl = account.lastBillingRecord.stripe_charge_receipt_url
        }

        const date = moment(account.lastBillingRecord.created_at).format('LL')

        return !receiptUrl ? (
            date
        ) : (
            <a href={receiptUrl} target="_blank">
                {date}
            </a>
        )
    }

    protected renderRowActions(account: BillingTableDataDto): React.ReactNode {
        return (
            <Dropdown.Button
                size="small"
                onClick={() => this.handlePrimaryActionClick(account)}
                overlay={
                    <Menu onClick={({ key, domEvent }) => this.handleDropdownActionClick(key, account, domEvent)}>
                        <Menu.Item key="history">
                            <DollarOutlined />
                            <span>Billing History</span>
                        </Menu.Item>
                    </Menu>
                }
            >
                <span>New Charge</span>
            </Dropdown.Button>
        )
    }

    protected handleChange = (pagination: any, filters: any, sorter: any) => {
        this.setState({
            sortedInfo: sorter,
        })
    }

    protected handlePrimaryActionClick(account: BillingTableDataDto): void {
        this.setState({ activeAccount: account })
    }

    protected handleDropdownActionClick = (
        key: string | number,
        account: BillingTableDataDto,
        domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
        if (key === 'history') {
            if (domEvent.metaKey) {
                window.open(this.buildBillingHistoryUrl(account))
            } else {
                this.jumpToBillingHistory(account)
            }
        }
    }

    protected buildBillingHistoryUrl(account: BillingTableDataDto): string {
        return `/organizations/${account.id}#billing`
    }

    protected jumpToBillingHistory(account: BillingTableDataDto) {
        this.appService.route(this.buildBillingHistoryUrl(account))
    }

    protected async fetchAccounts() {
        this.setState({ loading: true, accountsLoaded: false })
        let update: Partial<IAccountsState>

        try {
            const { ok, data, meta } =
                (await this.accountService.fetchBillable(
                    {
                        pagination: 0,
                        status: ['active'].join(','),
                        billable_period: this.state.unchargedSince,
                    },
                    false,
                )) || {}

            if (ok) {
                const accountDtos: BillingTableDataDto[] = data.map((account: AccountDto) => {
                    return BillingTableDataDto.fromAccountDto(account)
                })

                update = {
                    accountsLoaded: true,
                    allAccounts: accountDtos,
                    visibleAccounts: accountDtos,
                    accountsMeta: meta,
                }
            }
        } catch (error) {
            console.log(error)
        }

        await this.setState({
            loading: false,
            ...(update as any),
        })

        if (this.state.allAccounts.length > 0) {
            this.state.allAccounts.forEach((account, idx) => this.fetchAccountDependencies(account, idx))
        }
    }

    protected async fetchAccountDependencies(account: BillingTableDataDto, idx: number) {
        const update = clone(account)
        update.loadingDependecies = false

        const processes: Array<Promise<any>> = []

        processes.push(
            this.accountService
                .fetchBillingRecordsByAccountId(
                    account.id,
                    {
                        limit: 1,
                        order: JSON.stringify({
                            field: 'record.created_at',
                            direction: 'desc',
                        }),
                        processed: 1,
                    },
                    false,
                )
                .then(({ ok, data }) => {
                    if (ok) {
                        update.lastBillingRecord = data.length > 0 ? data[0] : null
                    }
                }),
        )

        const lastMonth = moment().subtract(1, 'month')
        processes.push(
            this.accountService
                .fetchSubscriberHistoryByAccountId(
                    account.id,
                    {
                        date_range: JSON.stringify({
                            since: lastMonth.startOf('month').format('YYYY-MM-DD'),
                            through: lastMonth.endOf('month').format('YYYY-MM-DD'),
                        }),
                    },
                    false,
                )
                .then(({ ok, data }) => {
                    if (ok) {
                        update.lastMonthsBillableSubscriberCount = data.length > 0 ? data[0].subscribed_count : 28045
                    }
                }),
        )

        await Promise.all(processes)

        return this.setState(({ allAccounts }) => {
            allAccounts.splice(idx, 1, update)

            return { allAccounts }
        })
    }
}
